import React from "react";
import {useTranslation} from "react-i18next";
import {useSales} from "./SalesContext";
import Accordion from "@/components/Accordion";
import AccordionItem from "@/components/AccordionItem";
import {AcademicCapIcon, BuildingLibraryIcon, GlobeAltIcon, UsersIcon,} from "@heroicons/react/24/outline";
import Loader from "@/components/Loader";
import useCoreSiteHealth from "@/hooks/useCoreSiteHealth";
import ContactListItem from "@/pages/workspaces/sales/ContactListItem";
import SiteListItem from "@/pages/workspaces/sales/SiteListItem";

function getSectionIcon(section) {
  const key = section?.toLowerCase();
  switch (key) {
    case 'network':
      return GlobeAltIcon;
    case 'single_site':
      return BuildingLibraryIcon;
    case 'educators':
      return AcademicCapIcon;
    case 'partners':
      return UsersIcon;
    default:
      return null;
  }
}

function SectionListItem({sectionKey, item}) {
  const {t} = useTranslation();

  if (sectionKey === 'network') return <div>{item.name}</div>;
  if (sectionKey === 'single_site') return <SiteListItem site={item}/>;
  if (sectionKey === 'educators') return <ContactListItem contact={{...item, title: t('educator')}}/>;
  if (sectionKey === 'partners') return <ContactListItem contact={{...item, title: t('partner')}}/>;
  return null
}

export default function SiteHealth() {
  const {t} = useTranslation();
  const {state} = useSales();
  const {clickedItem} = state;
  const siteId = clickedItem?.properties?.id;

  const {
    data: health,
    isLoading: isLoadingHealth,
    isError: isHealthError,
    error: healthError
  } = useCoreSiteHealth(siteId);

  if (!clickedItem || clickedItem.cluster) return null;

  return (
    <div className="space-y-1">
      <p className="font-semibold text-base-content">
        {t('workspace.sales.site_health')}
      </p>
      <p className="text-base text-gray-700">
        {t('workspace.sales.site_health_sub')}
      </p>
      {/*{clickedItem && <pre>{JSON.stringify(clickedItem, null, 2)}</pre>}*/}

      {isLoadingHealth && (
        <p className="text-sm test-base-content animate-pulse">
          <Loader/>
        </p>
      )}

      {isHealthError && (
        <p className="text-sm text-error">
          {t('error')}: {healthError?.message}
        </p>
      )}

      {health && (
        <div className="space-y-4">
          <Accordion>
            {['network', 'single_site', 'educators', 'partners'].map((sectionKey) => {
              const section = health[sectionKey];
              const icon = getSectionIcon(sectionKey);
              const translationKey = sectionKey.endsWith('s') ? sectionKey.slice(0, -1) : sectionKey;

              return (
                <AccordionItem
                  key={`aci-${siteId}-${sectionKey}`}
                  id={`aci-${siteId}-${sectionKey}`}
                  title={t(translationKey, {count: 2})}
                  badge={section.total}
                  icon={icon}
                >
                  <ul className="text-xs space-y-1 mt-1">
                    {section.data.map((item) => (
                      <SectionListItem key={item.id} item={item} sectionKey={sectionKey}/>
                    ))}
                    {section.total === 0 && (
                      <div className="text-base-content italic">
                        {t(`workspace.sales.no_${sectionKey}`)}
                      </div>
                    )}
                    {section.total > section.data.length && (
                      <div className="text-base-content italic">
                        + {section.total - section.data.length} {t('workspace.sales.more_ellipsis')}
                      </div>
                    )}
                  </ul>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}


    </div>
  );
}
